export interface BrandProductsPrefs {
  columns: string[];
  sort: { key: string; dir: 'asc' | 'desc' };
  viewMode: 'list' | 'grid';
}

/** Default column set used when no user preference exists. */
export const DEFAULT_BRAND_PRODUCTS_COLUMNS: readonly string[] = [
  'name',
  'ean',
  'price',
  'vendors',
  'rating',
  'salesonline30d',
];

export const DEFAULT_BRAND_PRODUCTS_PREFS: BrandProductsPrefs = {
  columns: [...DEFAULT_BRAND_PRODUCTS_COLUMNS],
  sort: { key: 'salesonline30d', dir: 'desc' },
  viewMode: 'list',
};

interface RawBrandProductsPrefs {
  columns: unknown[];
  sort: { key: unknown; dir: unknown };
  viewMode: unknown;
}

function isShape(raw: unknown): raw is RawBrandProductsPrefs {
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return false;
  const r = raw as Record<string, unknown>;
  if (!Array.isArray(r['columns'])) return false;
  const sort = r['sort'];
  if (!sort || typeof sort !== 'object' || Array.isArray(sort)) return false;
  return true;
}

function defaultPrefs(): BrandProductsPrefs {
  return {
    columns: [...DEFAULT_BRAND_PRODUCTS_PREFS.columns],
    sort: { ...DEFAULT_BRAND_PRODUCTS_PREFS.sort },
    viewMode: DEFAULT_BRAND_PRODUCTS_PREFS.viewMode,
  };
}

export function mergeBrandProducts(raw: unknown): BrandProductsPrefs {
  if (!isShape(raw)) return defaultPrefs();

  const cleaned = (raw.columns as unknown[]).filter(
    (c): c is string => typeof c === 'string' && c.length > 0
  );
  const columns = cleaned.includes('name') ? cleaned : ['name', ...cleaned];

  const validKey = typeof raw.sort.key === 'string' && raw.sort.key.length > 0;
  const sort = validKey
    ? {
        key: raw.sort.key as string,
        dir: raw.sort.dir === 'desc' ? ('desc' as const) : ('asc' as const),
      }
    : { ...DEFAULT_BRAND_PRODUCTS_PREFS.sort };
  const viewMode: 'list' | 'grid' = raw.viewMode === 'grid' ? 'grid' : 'list';

  return { columns, sort, viewMode };
}
